"use client";

import { useEffect, useState } from "react";
import { usePathname } from "next/navigation";
import { capturePostHogEvent } from "@/lib/posthog-client";
import { POSTHOG_EVENTS, POSTHOG_SOURCE } from "@/lib/posthog-events";
import { useIsDesktopEligibleForPwaInstall } from "@/lib/useDevice";
import { useLanguage } from "@/lib/useLanguage";

type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
};

function isExploreSelectionOpen(): boolean {
  if (typeof document === "undefined") return false;
  return document.documentElement.dataset.exploreSelectionOpen === "true";
}

export function PwaInstallPrompt() {
  const pathname = usePathname() ?? "";
  const { t } = useLanguage();
  const isDesktopEligible = useIsDesktopEligibleForPwaInstall();
  const isExplore = pathname.startsWith("/explore");
  const [deferred, setDeferred] = useState<BeforeInstallPromptEvent | null>(null);
  const [dismissed, setDismissed] = useState(false);
  const [selectionOpen, setSelectionOpen] = useState(false);
  const pwa = t.product?.pwaInstall;

  useEffect(() => {
    if (typeof window === "undefined" || !isDesktopEligible) return;
    const handler = (e: Event) => {
      e.preventDefault();
      setDeferred(e as BeforeInstallPromptEvent);
      capturePostHogEvent(POSTHOG_EVENTS.PWA_INSTALL_PROMPTED, { source: POSTHOG_SOURCE });
    };
    window.addEventListener("beforeinstallprompt", handler);
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, [isDesktopEligible]);

  useEffect(() => {
    if (!isDesktopEligible) {
      setDeferred(null);
    }
  }, [isDesktopEligible]);

  useEffect(() => {
    if (!isExplore) return;
    const sync = () => setSelectionOpen(isExploreSelectionOpen());
    sync();
    const observer = new MutationObserver(sync);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["data-explore-selection-open"],
    });
    return () => observer.disconnect();
  }, [isExplore]);

  if (!isDesktopEligible || !deferred || dismissed) return null;
  if (isExplore && selectionOpen) return null;

  const positionClass = isExplore
    ? "bottom-20 left-4 right-4 md:bottom-4 md:left-4 md:right-auto md:max-w-sm"
    : "bottom-20 left-4 right-4 md:bottom-4 md:right-4 md:left-auto md:max-w-sm";

  return (
    <div
      className={`fixed z-40 rounded-xl border border-gray-200 bg-white p-4 shadow-lg dark:border-gray-700 dark:bg-gray-900 ${positionClass}`}
    >
      <p className="mb-2 text-sm font-medium">
        {pwa?.title ?? "Install MenúHunter web app"}
      </p>
      <p className="mb-3 text-xs text-gray-500">
        {pwa?.description ?? "Add MenúHunter to this computer for quick access."}
      </p>
      <div className="flex gap-2">
        <button
          type="button"
          className="flex-1 rounded-lg bg-accent py-2 text-sm font-semibold text-gray-900"
          onClick={() => {
            void deferred.prompt().then(() => {
              capturePostHogEvent(POSTHOG_EVENTS.PWA_INSTALLED, { source: POSTHOG_SOURCE });
              setDeferred(null);
            });
          }}
        >
          {pwa?.install ?? "Install web app"}
        </button>
        <button
          type="button"
          className="px-3 py-2 text-sm text-gray-500"
          onClick={() => setDismissed(true)}
        >
          {pwa?.dismiss ?? "Not now"}
        </button>
      </div>
    </div>
  );
}
